// src\pages\Illustration.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usePortfolio } from '../hooks/usePortfolio';
import {
  FiGrid,
  FiHeart,
  FiAlertCircle,
  FiArrowRight,
  FiImage,
  FiChevronDown,
  FiFilter,
  FiX,
  FiPenTool,
  FiStar,
  FiZap,
  FiAward,
  FiClock,
  FiPackage,
  FiLayers,
  FiTrendingUp,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import PortfolioSkeleton from '../components/portfolio/PortfolioSkeleton';
import StatsSection from '../components/StatsSection';
import ReviewsSection from '../components/ReviewsSection';
import { FiverrCard } from '../components/FiverrCard';
import { ogImageService } from '../services/ogImageService';

// =============================================
// ANIMATION VARIANTS
// =============================================
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

// =============================================
// CATEGORY LABEL MAP
// =============================================
const KNOWN_CATEGORY_LABELS = {
  'logo-recreation': 'Logo Recreation',
  'vector-tracing': 'Vector Tracing',
  'clipart-tracing': 'Clipart Tracing',
  'photo-to-vector': 'Photo to Vector',
  'line-art': 'Line Art',
  'portrait-tracing': 'Portrait Tracing',
  'sticker-design': 'Sticker Design',
  'icon-vectorization': 'Icon Vectorization',
  'signature-tracing': 'Signature Tracing',
  'silhouette-tracing': 'Silhouette Tracing',
  'pattern-tracing': 'Pattern Tracing',
  'sports-logo-tracing': 'Sports & Team Logos',
  others: 'Other Works',
  branding: 'Brand Design',
  product: 'Product Visual',
};

const formatCategoryLabel = slug => {
  if (!slug) return 'Untitled';
  const key = String(slug).toLowerCase().trim();
  if (KNOWN_CATEGORY_LABELS[key]) return KNOWN_CATEGORY_LABELS[key];
  return key
    .split(/[-_]/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// =============================================
// GLASSY DIVIDER (matches Portfolio.jsx)
// =============================================
const GlassyDivider = () => (
  <div className="relative py-8 px-4">
    <div className="max-w-7xl mx-auto">
      <div className="relative h-px w-full overflow-hidden">
        <motion.div
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-0 w-1/3"
          style={{
            background:
              'linear-gradient(to right, transparent, var(--text), transparent)',
            opacity: 0.1,
          }}
        />
      </div>
    </div>
  </div>
);

// =============================================
// GLASSY CARD (matches Portfolio.jsx)
// =============================================
const GlassyCard = ({ children, className = '' }) => (
  <div
    className={`relative overflow-hidden backdrop-blur-xl ${className}`}
    style={{
      backgroundColor: 'rgba(var(--bg-rgb), 0.4)',
      border: '1px solid rgba(var(--text-rgb), 0.06)',
      boxShadow:
        '0 8px 32px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    }}
  >
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
      <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
    </div>
    {children}
  </div>
);

// =============================================
// HERO — Same liquid gradient style as Portfolio
// =============================================
const IllustrationHero = ({ totalProjects = 0 }) => (
  <div className="relative mb-8 overflow-hidden rounded-3xl">
    {/* Glassy gradient background */}
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:via-transparent dark:to-purple-500/10" />

    {/* Animated liquid blobs — same palette as Portfolio */}
    <motion.div
      animate={{
        x: ['-20%', '15%', '-5%', '20%', '-20%'],
        y: ['-15%', '10%', '-10%', '15%', '-15%'],
        scale: [1, 1.3, 0.9, 1.2, 1],
        borderRadius: [
          '60% 40% 30% 70%',
          '40% 60% 70% 30%',
          '30% 70% 60% 40%',
          '70% 30% 40% 60%',
          '60% 40% 30% 70%',
        ],
      }}
      transition={{
        duration: 15,
        repeat: Infinity,
        ease: 'easeInOut',
        times: [0, 0.25, 0.5, 0.75, 1],
      }}
      className="absolute top-5 left-5 w-72 h-72 blur-3xl"
      style={{
        background:
          'radial-gradient(circle, #3b82f6 0%, #60a5fa 40%, transparent 70%)',
        opacity: 0.12,
      }}
    />

    <motion.div
      animate={{
        x: ['15%', '-15%', '10%', '-10%', '15%'],
        y: ['15%', '-10%', '20%', '-15%', '15%'],
        scale: [1.2, 0.9, 1.4, 0.8, 1.2],
        borderRadius: [
          '40% 60% 70% 30%',
          '60% 40% 30% 70%',
          '70% 30% 40% 60%',
          '30% 70% 60% 40%',
          '40% 60% 70% 30%',
        ],
      }}
      transition={{
        duration: 18,
        repeat: Infinity,
        ease: 'easeInOut',
        times: [0, 0.25, 0.5, 0.75, 1],
      }}
      className="absolute bottom-5 right-5 w-80 h-80 blur-3xl"
      style={{
        background:
          'radial-gradient(circle, #8b5cf6 0%, #a78bfa 40%, transparent 70%)',
        opacity: 0.1,
      }}
    />

    <motion.div
      animate={{
        x: ['-5%', '10%', '-10%', '5%', '-5%'],
        y: ['10%', '-15%', '5%', '-10%', '10%'],
        scale: [1, 1.2, 0.8, 1.3, 1],
        borderRadius: [
          '50% 30% 60% 40%',
          '30% 60% 40% 70%',
          '60% 40% 70% 30%',
          '40% 70% 30% 60%',
          '50% 30% 60% 40%',
        ],
      }}
      transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'easeInOut',
        times: [0, 0.25, 0.5, 0.75, 1],
      }}
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 blur-3xl"
      style={{
        background:
          'radial-gradient(circle, #06b6d4 0%, #22d3ee 40%, transparent 70%)',
        opacity: 0.08,
      }}
    />

    <motion.div
      animate={{
        x: ['10%', '-20%', '15%', '-5%', '10%'],
        y: ['-5%', '15%', '-15%', '10%', '-5%'],
        scale: [0.8, 1.4, 0.9, 1.1, 0.8],
        borderRadius: [
          '70% 30% 50% 50%',
          '30% 70% 50% 50%',
          '50% 50% 70% 30%',
          '50% 50% 30% 70%',
          '70% 30% 50% 50%',
        ],
      }}
      transition={{
        duration: 22,
        repeat: Infinity,
        ease: 'easeInOut',
        times: [0, 0.25, 0.5, 0.75, 1],
      }}
      className="absolute bottom-20 left-20 w-60 h-60 blur-3xl"
      style={{
        background:
          'radial-gradient(circle, #ec4899 0%, #f472b6 40%, transparent 70%)',
        opacity: 0.06,
      }}
    />

    {/* Content */}
    <div className="relative pt-34 max-sm:pt-20 pb-20 md:py-16 lg:py-20 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-sm backdrop-blur-sm"
        style={{
          backgroundColor: 'rgba(var(--bg-rgb), 0.6)',
          border: '1px solid rgba(var(--text-rgb), 0.06)',
        }}
      >
        <FiPenTool className="w-4 h-4" style={{ color: '#3b82f6' }} />
        <span style={{ color: 'var(--muted)' }}>Image Tracing Studio</span>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight leading-[1.1]"
        style={{ color: 'var(--text)' }}
      >
        Precision
        <span className="block" style={{ color: 'var(--muted)' }}>
          Image Tracing
        </span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
        style={{ color: 'var(--muted)', opacity: 0.8 }}
      >
        Hand-traced vectors for logos, artwork, and illustrations — clean,
        scalable, and delivered in every format you need.
      </motion.p>

      {/* Trust strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs"
        style={{ color: 'var(--muted)' }}
      >
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.5)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiClock className="w-3 h-3" style={{ color: '#3b82f6' }} />
          24–48h Delivery
        </span>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.5)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiAward className="w-3 h-3" style={{ color: '#8b5cf6' }} />
          Unlimited Revisions
        </span>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.5)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiPackage className="w-3 h-3" style={{ color: '#06b6d4' }} />
          All Source Files
        </span>
      </motion.div>

      {/* Minimal decorative line */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="mt-8 flex items-center justify-center gap-3"
      >
        <span
          className="w-12 h-px"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <span
          className="text-xs tracking-widest uppercase"
          style={{ color: 'var(--muted)', opacity: 0.3 }}
        >
          Explore
        </span>
        <span
          className="w-12 h-px"
          style={{ backgroundColor: 'var(--border)' }}
        />
      </motion.div>
    </div>
  </div>
);

// =============================================
// MAIN COMPONENT
// =============================================
function Illustration() {
  const { projects, allProjects, loading, error, trackClick } =
    usePortfolio('illustration');

  const [hoveredId, setHoveredId] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState({});

  // Filter state
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const totalProjectsCount = allProjects?.length || 0;

  // OG meta
  const metaData = useMemo(() => ogImageService.portfolioHome(), []);

  // =============================================
  // DYNAMIC CATEGORIES FROM LOADED PROJECTS
  // =============================================
  const categories = useMemo(() => {
    const counts = {};
    (allProjects || []).forEach(p => {
      const cat = p.category;
      if (cat) counts[cat] = (counts[cat] || 0) + 1;
    });

    const list = [
      {
        id: 'all',
        name: 'All Illustrations',
        count: allProjects?.length || 0,
      },
    ];

    Object.entries(counts)
      .sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1];
        return a[0].localeCompare(b[0]);
      })
      .forEach(([slug, count]) => {
        list.push({
          id: slug,
          name: formatCategoryLabel(slug),
          count,
        });
      });

    return list;
  }, [allProjects]);

  // Filtered
  const filteredProjects = useMemo(() => {
    if (selectedCategory === 'all') return projects || [];
    return (projects || []).filter(p => p.category === selectedCategory);
  }, [projects, selectedCategory]);

  const filteredCount = filteredProjects.length;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset filter if selected category disappears
  useEffect(() => {
    if (selectedCategory === 'all') return;
    const exists = categories.some(c => c.id === selectedCategory);
    if (!exists) setSelectedCategory('all');
  }, [categories, selectedCategory]);

  // =============================================
  // IMAGE HELPERS
  // =============================================
  const handleImageError = (projectId, imageIndex = 0) => {
    const key = `${projectId}_${imageIndex}`;
    setImageErrors(prev => ({ ...prev, [key]: true }));
  };

  const handleImageLoad = (projectId, imageIndex = 0) => {
    const key = `${projectId}_${imageIndex}`;
    setImagesLoaded(prev => ({ ...prev, [key]: true }));
  };

  const getCleanCloudinaryUrl = url => {
    if (!url) return url;
    if (!url.includes('cloudinary.com')) return url;
    try {
      const uploadMatch = url.match(/(upload\/v\d+\/)/);
      if (!uploadMatch) return url;
      const uploadPart = uploadMatch[0];
      const afterUpload = url.split(uploadPart)[1];
      const cleanPath = afterUpload
        .split('/')
        .filter(part => !part.match(/^(w_|h_|c_|f_|q_|dpr_|g_|e_|o_|ar_|fl_)/))
        .join('/');
      const baseUrl = url.split(uploadPart)[0];
      return `${baseUrl}${uploadPart}${cleanPath}`;
    } catch {
      return url;
    }
  };

  const getThumbnailAspectRatio = project => {
    if (project.images && project.images.length > 0) {
      const thumbnailUrl = project.thumbnailUrl || project.images[0]?.url;
      if (thumbnailUrl) {
        const matchedImage = project.images.find(
          img =>
            img.url === thumbnailUrl ||
            (img.publicId && thumbnailUrl.includes(img.publicId)),
        );
        if (matchedImage) {
          if (matchedImage.ratio) return matchedImage.ratio;
          if (matchedImage.width && matchedImage.height) {
            return matchedImage.width / matchedImage.height;
          }
        }
      }
      const firstImage = project.images[0];
      if (firstImage) {
        if (firstImage.ratio) return firstImage.ratio;
        if (firstImage.width && firstImage.height) {
          return firstImage.width / firstImage.height;
        }
      }
    }
    if (project.ratio) return project.ratio;
    return 4 / 3;
  };

  const getThumbnailUrl = project => {
    let url = project.thumbnailUrl;
    if (!url && project.images && project.images.length > 0) {
      url = project.images[0].url;
    }
    if (!url) url = project.imageUrl || '';
    return getCleanCloudinaryUrl(url);
  };

  const handleProjectClick = async project => {
    try {
      await trackClick(project.id);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  // =============================================
  // DROPDOWN
  // =============================================
  const currentCategoryLabel = useMemo(() => {
    const found = categories.find(c => c.id === selectedCategory);
    return found ? found.name : 'All Illustrations';
  }, [categories, selectedCategory]);

  const toggleDropdown = () => {
    setActiveDropdown(activeDropdown === 'category' ? null : 'category');
  };

  const handleCategorySelect = id => {
    setSelectedCategory(id);
    setActiveDropdown(null);
  };

  return (
    <>
      <Helmet>
        <title>
          {metaData?.metaTitle
            ? `Illustrations - ${metaData.metaTitle}`
            : 'Illustrations - WD Hajbul | Precision Image Tracing'}
        </title>
        <meta
          name="description"
          content="Professional image tracing services — logo recreation, vector art, and clean scalable illustrations delivered in every format."
        />
        <meta
          name="keywords"
          content="image tracing, vector tracing, logo recreation, digital art, illustration services, vector art"
        />
        <link
          rel="canonical"
          href={metaData?.canonical || window.location.href}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:title"
          content="Illustrations - WD Hajbul | Precision Image Tracing"
        />
        <meta
          property="og:description"
          content="Professional image tracing services — logo recreation, vector art, and clean scalable illustrations."
        />
        <meta property="og:image" content={metaData?.og?.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Illustrations - WD Hajbul" />
        <meta
          name="twitter:description"
          content="Professional image tracing services — logo recreation, vector art, and clean scalable illustrations."
        />
        <meta name="twitter:image" content={metaData?.twitter?.image} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* Hero */}
        <section className="pt-32 px-4 max-w-7xl mx-auto max-sm:p-5 max-sm:pt-24">
          <IllustrationHero totalProjects={totalProjectsCount} />
        </section>

        {/* Stats — same component as portfolio */}
        <StatsSection
          fetchProjectCount={true}
          columns={4}
          variant="default"
          showBorder={true}
        />

        {/* Divider */}
        <GlassyDivider />

        {/* ============================================= */}
        {/* FILTER BAR (sticky) — same design as Portfolio */}
        {/* ============================================= */}
        <div
          className="sticky top-20 z-20 py-4 px-4"
          style={{
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div
                className="flex items-center gap-3 flex-wrap"
                ref={dropdownRef}
              >
                {/* Category Dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={toggleDropdown}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 hover:opacity-80"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <FiFilter className="w-3.5 h-3.5" />
                    <span>{currentCategoryLabel}</span>
                    <FiChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-300 ${
                        activeDropdown === 'category' ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {activeDropdown === 'category' && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 min-w-[260px] max-h-[420px] overflow-y-auto rounded-2xl shadow-xl z-50"
                        style={{
                          backgroundColor: 'var(--bg)',
                          border: '1px solid var(--border)',
                          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <div className="p-1.5">
                          {categories.map(category => (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => handleCategorySelect(category.id)}
                              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                                selectedCategory === category.id
                                  ? 'bg-black/10 dark:bg-white/10'
                                  : 'hover:bg-black/5 dark:hover:bg-white/5'
                              }`}
                              style={{
                                color:
                                  selectedCategory === category.id
                                    ? 'var(--text)'
                                    : 'var(--muted)',
                              }}
                            >
                              <span className="truncate">{category.name}</span>
                              <span className="text-xs opacity-60 ml-2 flex-shrink-0">
                                ({category.count})
                              </span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Clear Filter */}
                {selectedCategory !== 'all' && (
                  <button
                    type="button"
                    onClick={() => setSelectedCategory('all')}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs transition-all duration-300 hover:opacity-70"
                    style={{
                      color: 'var(--muted)',
                      border: '1px solid var(--border)',
                      backgroundColor: 'var(--border)',
                    }}
                  >
                    <FiX className="w-3 h-3" />
                    Clear Filter
                  </button>
                )}

                {/* Back to Portfolio */}
                <Link
                  to="/portfolio"
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <FiGrid className="w-3.5 h-3.5" />
                  <span>Portfolio</span>
                </Link>
              </div>

              {/* Results Count */}
              <div
                className="text-xs hidden sm:block px-3 py-1 rounded-full"
                style={{
                  color: 'var(--muted)',
                  backgroundColor: 'var(--border)',
                  border: '1px solid var(--border)',
                }}
              >
                {filteredCount} illustration{filteredCount === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        </div>

        {/* ============================================= */}
        {/* MASONRY GALLERY GRID */}
        {/* ============================================= */}
        <section className="py-8 px-4 max-w-7xl mx-auto">
          {loading ? (
            <PortfolioSkeleton />
          ) : error ? (
            <div className="text-center py-20">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiAlertCircle
                  className="w-10 h-10"
                  style={{ color: 'var(--muted)' }}
                />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Unable to load illustrations
              </h3>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                {error}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-6 py-2 rounded-full text-sm"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                Try Again
              </button>
            </div>
          ) : !filteredProjects || filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiPenTool
                  className="w-10 h-10"
                  style={{ color: 'var(--muted)' }}
                />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {selectedCategory === 'all'
                  ? 'No illustrations yet'
                  : 'No illustrations in this category'}
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                {selectedCategory === 'all'
                  ? 'Check back soon for new work!'
                  : 'Try a different category or view all illustrations.'}
              </p>
              {selectedCategory !== 'all' && (
                <button
                  type="button"
                  onClick={() => setSelectedCategory('all')}
                  className="px-4 py-2 rounded-full text-sm transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                  }}
                >
                  View All Illustrations
                </button>
              )}
            </div>
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="columns-1 sm:columns-2 lg:columns-3 gap-6 [&>div]:mb-6"
            >
              {filteredProjects.map((project, index) => {
                const aspectRatio = getThumbnailAspectRatio(project);
                const isHovered = hoveredId === project.id;
                const thumbnailUrl = getThumbnailUrl(project);
                const imageKey = `${project.id}_0`;
                const categoryLabel = formatCategoryLabel(project.category);
                const imageCount = project.images?.length || 1;

                return (
                  <motion.div
                    key={project.id}
                    variants={fadeInUp}
                    transition={{ delay: index * 0.05 }}
                    className="break-inside-avoid group relative cursor-pointer rounded-xl overflow-hidden"
                    onMouseEnter={() => setHoveredId(project.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <Link
                      to={`/illustrations/${project.id}`}
                      onClick={() => handleProjectClick(project)}
                      className="block"
                    >
                      <div
                        className="relative overflow-hidden w-full"
                        style={{
                          backgroundColor: 'transparent',
                          aspectRatio: aspectRatio,
                        }}
                      >
                        {/* Loader */}
                        {!imagesLoaded[imageKey] && !imageErrors[imageKey] && (
                          <div className="absolute inset-0 flex items-center justify-center z-10">
                            <div
                              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
                              style={{ borderColor: 'var(--text)' }}
                            />
                          </div>
                        )}

                        <img
                          src={
                            imageErrors[imageKey]
                              ? 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E'
                              : thumbnailUrl
                          }
                          alt={`${project.seoTitle || project.title} - ${categoryLabel}`}
                          className="w-full h-full transition-transform duration-700 group-hover:scale-105"
                          style={{
                            objectFit: 'cover',
                            display: 'block',
                            opacity: imagesLoaded[imageKey] ? 1 : 0,
                            width: '100%',
                            height: '100%',
                          }}
                          loading="lazy"
                          onLoad={() => handleImageLoad(project.id, 0)}
                          onError={() => handleImageError(project.id, 0)}
                        />

                        {/* Image Count Badge — Glassy (matches Portfolio) */}
                        {imageCount > 1 && (
                          <div
                            className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-md backdrop-blur-md text-xs z-10"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.06)',
                            }}
                          >
                            <FiImage className="w-3 h-3" />
                            <span>{imageCount}</span>
                          </div>
                        )}

                        {/* Category Badge — Glassy */}
                        <div
                          className="absolute top-3 left-3 px-2 py-0.5 rounded-md text-xs font-medium backdrop-blur-md z-10"
                          style={{
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.06)',
                          }}
                        >
                          {categoryLabel}
                        </div>

                        {/* Featured star badge */}
                        {project.featured && (
                          <div
                            className="absolute bottom-3 left-3 px-2 py-0.5 rounded-md text-xs font-medium backdrop-blur-md z-10 flex items-center gap-1"
                            style={{
                              backgroundColor: 'rgba(234,179,8,0.85)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.1)',
                            }}
                          >
                            <FiStar className="w-2.5 h-2.5 fill-current" />
                            <span>Featured</span>
                          </div>
                        )}

                        {/* Like Count Badge — Glassy (matches Portfolio) */}
                        <div
                          className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full backdrop-blur-md text-xs z-10"
                          style={{
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.06)',
                          }}
                        >
                          <FiHeart className="w-2.5 h-2.5" />
                          <span>{project.likes || 0}</span>
                        </div>

                        {/* Hover Overlay — Glassy */}
                        <div
                          className={`absolute inset-0 transition-all duration-500 z-10 ${
                            isHovered ? 'opacity-100' : 'opacity-0'
                          }`}
                          style={{
                            backgroundColor: 'rgba(0,0,0,0.75)',
                            backdropFilter: 'blur(8px)',
                          }}
                        >
                          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                            <div className="transform transition-all duration-500 translate-y-4 group-hover:translate-y-0">
                              <h3 className="text-white text-xl font-bold mb-2 line-clamp-2">
                                {project.title}
                              </h3>
                              <p className="text-white/80 text-sm mb-4 line-clamp-3">
                                {project.description}
                              </p>
                              <div className="flex items-center justify-center gap-2 flex-wrap">
                                <span className="text-xs text-white/60">
                                  {categoryLabel}
                                </span>
                                <span className="text-white/60">•</span>
                                <span className="text-xs text-white/60">
                                  {new Date(project.createdAt).getFullYear()}
                                </span>
                                {imageCount > 1 && (
                                  <>
                                    <span className="text-white/60">•</span>
                                    <span className="text-xs text-white/60 flex items-center gap-1">
                                      <FiImage className="w-3 h-3" />
                                      {imageCount}
                                    </span>
                                  </>
                                )}
                              </div>
                              <span className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white text-black hover:bg-white/90 transition-colors">
                                View Illustration{' '}
                                <FiArrowRight className="w-3 h-3" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </section>

        {/* ============================================= */}
        {/* TRUST / PROCESS SECTION */}
        {/* ============================================= */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={fadeInUp}
              className="text-center mb-12"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm backdrop-blur-sm"
                style={{
                  backgroundColor: 'rgba(var(--bg-rgb), 0.6)',
                  border: '1px solid rgba(var(--text-rgb), 0.06)',
                }}
              >
                <FiZap className="w-4 h-4" style={{ color: '#3b82f6' }} />
                <span style={{ color: 'var(--muted)' }}>
                  Why clients choose me
                </span>
              </div>
              <h2
                className="text-3xl lg:text-4xl font-bold tracking-tight mb-3"
                style={{ color: 'var(--text)' }}
              >
                Built for professionals
              </h2>
              <p
                className="text-sm max-w-lg mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                Clean vectors, fast delivery, and everything you need to use
                your artwork everywhere.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={staggerContainer}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            >
              {[
                {
                  icon: FiLayers,
                  title: 'Hand-Traced Precision',
                  description:
                    'Every curve manually refined — no auto-trace artifacts.',
                  color: '#3b82f6',
                },
                {
                  icon: FiPackage,
                  title: 'All Source Files',
                  description:
                    'AI, EPS, SVG, PDF, PNG — ready for any production.',
                  color: '#8b5cf6',
                },
                {
                  icon: FiClock,
                  title: '24–48h Turnaround',
                  description:
                    'Standard delivery in under two days. Rush available.',
                  color: '#06b6d4',
                },
                {
                  icon: FiAward,
                  title: 'Unlimited Revisions',
                  description:
                    'Refine until it is perfect — no limits, no extra fees.',
                  color: '#ec4899',
                },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={idx}
                    variants={fadeInUp}
                    className="relative overflow-hidden backdrop-blur-xl rounded-2xl p-5 transition-all hover:scale-[1.02]"
                    style={{
                      backgroundColor: 'rgba(var(--bg-rgb), 0.4)',
                      border: '1px solid rgba(var(--text-rgb), 0.06)',
                      boxShadow:
                        '0 8px 32px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                      style={{
                        backgroundColor: `${item.color}15`,
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: item.color }} />
                    </div>
                    <h3
                      className="font-semibold text-sm mb-1.5"
                      style={{ color: 'var(--text)' }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-xs leading-relaxed"
                      style={{ color: 'var(--muted)' }}
                    >
                      {item.description}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        <GlassyDivider />

        <ReviewsSection />

        <GlassyDivider />

        {/* Fiverr Card — matches Portfolio */}
        <section className="py-8 px-4 max-w-7xl mx-auto">
          <FiverrCard variant="standard" />
        </section>

        {/* ============================================= */}
        {/* CTA SECTION — Same glassy card as Portfolio */}
        {/* ============================================= */}
        <section className="py-16 px-4">
          <GlassyCard className="max-w-4xl mx-auto text-center p-12 rounded-3xl relative overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                opacity: 0.05,
              }}
            />

            <div className="relative z-10">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5 text-xs font-medium uppercase tracking-wider"
                style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  color: '#3b82f6',
                }}
              >
                <FiTrendingUp className="w-3 h-3" />
                Ready to start?
              </div>

              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Have an image that needs tracing?
              </h2>
              <p
                className="mb-6 max-w-md mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                From logo recreation to full vector illustrations — I'll
                transform any raster artwork into clean, scalable vectors.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <motion.a
                  whileHover={{ scale: 1.03, gap: '0.75rem' }}
                  whileTap={{ scale: 0.97 }}
                  href="/hire"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all duration-300"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                >
                  Start a Project
                  <FiArrowRight className="w-4 h-4" />
                </motion.a>
                <Link
                  to="/portfolio"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all duration-300 hover:gap-3"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <FiGrid className="w-4 h-4" />
                  View Portfolio
                </Link>
              </div>
            </div>
          </GlassyCard>
        </section>
      </div>
    </>
  );
}

export default Illustration;
